import { Container } from '@hg/stylizator/components'
import { useSizes } from '@hg/stylizator/hooks'
import { Text } from '@hg/stylizator/typography'
import { Flex, desktop } from '@hg/stylizator/utils'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import parse from 'html-react-parser'
import * as React from 'react'
import styled, { useTheme } from 'styled-components'

import Post from './Post'
import ViewAllLink from './ViewAllLink'

import ClientOnly from 'app/components/ClientOnly'
import { Post as IPost } from 'app/service/api/theme/post'

dayjs.extend(localizedFormat)

interface IProps {
  posts: IPost[]
  title: string
}

const RelatedPosts: React.FC<IProps> = ({ title, posts: originalPosts }) => {
  const theme = useTheme()
  const { isDesktop } = useSizes()

  const size = isDesktop ? 3 : 4
  const postsData = originalPosts.slice(0, size)

  const posts = React.useMemo(
    () =>
      postsData.map((it) => {
        const thumbnailImage = parse(it.thumbnail, { trim: true }) as React.ReactElement
        const { height, width, ...thumbnailProps } = thumbnailImage?.props ?? {}

        return {
          ...it,
          thumbnailProps,
        }
      }),
    [postsData],
  )

  if (!posts.length) {
    return null
  }

  return (
    <Background>
      <Container
        margins={{
          mobile: 0,
          tablet: 0,
          desktop: 0,
          largeDesktop: 0,
        }}
        style={{ padding: '0 16px', boxSizing: 'border-box' }}
      >
        <Flex justify="space-between" align="center">
          <Text type="heading_medium" useCompact>
            <h3>{title}</h3>
          </Text>

          <Text type="compact_label" color={theme.color.blue[60]}>
            <ViewAllLink href="/blog" />
          </Text>
        </Flex>
        <List count={size}>
          <ClientOnly>
            {posts.map((post) => (
              <Post key={post.title} post={post} variant="medium" titleHeading="h4" />
            ))}
          </ClientOnly>
        </List>
      </Container>
    </Background>
  )
}

export default RelatedPosts

const Background = styled.div`
  padding: 48px 0;
  background: ${(props) => props.theme.color.blue[10]};

  @media ${desktop} {
    padding: 72px 0;
  }
`

const List = styled.div<{ count?: number }>`
  margin: 24px -16px 0;
  padding: 0 16px;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: ${(props) => `repeat(${props.count ?? 4}, 1fr)`};
  gap: 16px;
  overflow-x: auto;

  @media ${desktop} {
    margin-top: 32px;
    gap: 24px;
    overflow-x: hidden;
    grid-template-columns: repeat(3, 1fr);
  }

  & > div {
    min-width: 220px;

    @media ${desktop} {
      min-width: auto;
    }
  }
`
